import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import TopNav from '../components/TopNav';
import './SelectApartment.css';

export default function SelectApartment() {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [apartmentId, setApartmentId] = useState(userProfile?.apartmentId || '');
  const [flatNumber, setFlatNumber] = useState(userProfile?.flatNumber || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const snapshot = await getDocs(collection(db, 'apartments'));
        setApartments(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError('Could not load apartments.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (userProfile?.apartmentId) setApartmentId(userProfile.apartmentId);
    if (userProfile?.flatNumber) setFlatNumber(userProfile.flatNumber);
  }, [userProfile?.apartmentId, userProfile?.flatNumber]);

  const selectedApartment = apartments.find(a => a.id === apartmentId);
  const flatNumbers = selectedApartment?.flatNumbers || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!apartmentId || !flatNumber) {
      setError('Please select apartment and flat number.');
      return;
    }
    if (!currentUser?.uid) {
      setError('Not signed in.');
      return;
    }
    setSaving(true);
    try {
      const apt = apartments.find(a => a.id === apartmentId);
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        email: currentUser.email,
        apartmentId,
        apartmentName: apt?.name || '',
        apartmentShortCode: apt?.shortCode || '',
        flatNumber,
        apartmentApproved: false
      }, { merge: true });
      if (refreshUserProfile) await refreshUserProfile();
      navigate('/');
    } catch (err) {
      const msg = err?.message || 'Failed to save.';
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(msg);
      setError(isPermission
        ? 'You don’t have permission to save. Sign in again and try again, or contact an admin.'
        : msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const alreadySet = !!(userProfile?.apartmentId && userProfile?.flatNumber);

  return (
    <div className="select-apartment-container">
      <TopNav title="Select location" />
      <div className="select-apartment-card">
        <h1>Apartment & flat</h1>
        {alreadySet ? (
          <>
            <p className="select-apartment-subtitle">
              Your apartment and flat are set. Only an admin can change them.
            </p>
            <p style={{ margin: '16px 0', fontSize: '1.1rem' }}>
              <strong>{userProfile?.apartmentName || '—'}</strong>, {userProfile?.flatNumber || '—'}
            </p>
            <button type="button" onClick={() => navigate('/')} className="btn-primary">
              Back to Dashboard
            </button>
          </>
        ) : apartments.length === 0 ? (
          <p className="select-apartment-empty">
            No apartments are set up yet. Ask an admin to add apartments under Admin → Apartments.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="select-apartment-subtitle">
              Choose your apartment and flat number. Only an admin can change this later.
            </p>
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label>Apartment</label>
              <select
                value={apartmentId}
                onChange={(e) => { setApartmentId(e.target.value); setFlatNumber(''); }}
                required
                className="select-apartment-input"
              >
                <option value="">Select apartment</option>
                {apartments.map(a => (
                  <option key={a.id} value={a.id}>{a.shortCode ? `${a.name} (${a.shortCode})` : a.name}</option>
                ))}
              </select>
            </div>
            {apartmentId && (
              <div className="form-group">
                <label>Flat number</label>
                <select
                  value={flatNumber}
                  onChange={(e) => setFlatNumber(e.target.value)}
                  required
                  className="select-apartment-input"
                >
                  <option value="">Select flat</option>
                  {flatNumbers.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            )}
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : 'Continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
